import Card from '../../components/card/Card';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  activateClientLines,
  getInstrumentationId,
  getInstrumentationStatus,
} from '../../services/InstrumentationService';
import { amount, clientData, Line, InstrumentationProgress, InstrumentationStatus, commentData } from '../../types/interfaces';
import LineStatusIndicator from './LineStatusIndicator';
import { ivaStatuses } from '../../mocks/IvaStatuses';
import { economicActivities } from '../../mocks/EconomicActivities';
import { formatCurrency } from '../../utils/Masks';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { hasData, isSuccessfulResponse } from '../../utils/Utils';
import Image from '../../components/image/Image';
import { getProductLines } from '../../services/ClientService';
import {
  parseSelectableProductLines,
  resolveSelectableProductLineLabel,
  UNAVAILABLE_PRODUCT_LINE_LABEL,
} from '../../utils/productLines';
import RejectApplicationModal from '../common/Modals/RejectApplicationModal';
import { APPLICATION_STATUS } from '../../utils/Constants';
import { Toast, useToast } from '../../components/toast/toast';
import { updateApplication } from '../../services/ApplicationService';
import {
  navigateAfterFlowAction,
  type DetailLocationState,
} from '../../utils/listNavigationState';
import CloseApplicationModal from './InstrumentationModals/CloseApplicationModal';
import Spinner from '../../components/Spinner/spinner';
import H from '../../components/texts/h/H';
import { useConfig } from '../../context/ConfigContext';
import {
  AVALES_INSTRUMENTATION_INFO_BANNER,
  getInstrumentationActivationErrorMessage,
  getLineWarningMessage,
  isLineSuccessInOneClickMode,
  ONECLICK_LINES_SECTION_WARNING,
  resolveLineProgress,
  shouldShowLineWarning,
  shouldSkipOneClickInstrumentationUi,
} from '../../utils/instrumentationLineStatus';

interface ClientProps {
  cuit: string | undefined;
  clientData: clientData;
  onPreviousClick: () => void;
  handleRetryGet: () => void;
  lines: Line[];
  applicationId: string | undefined;
  vigencia?: { startDate: string; endDate: string };
}

const ClientLines: React.FC<ClientProps> = ({
  clientData,
  lines,
  onPreviousClick,
  applicationId,
  vigencia,
}) => {
  const { cuit, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? {}) as DetailLocationState;
  const config = useConfig();
  const fiscalId = useFiscalIdConfig();
  const isGuaranteesEnabled = config.isGuaranteesEnabled ?? false;

  const [isAllActivated, setIsAllActivated] = useState<boolean>(false);
  const [isOneClickEnabled, setIsOneClickEnabled] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isActivatingLines, setIsActivatingLines] = useState<boolean>(false);
  const [instrumentationId, setInstrumentationId] = useState(undefined);
  const [hasAnyNullInstrumentation, setHasAnyNullInstrumentation] =
    useState<boolean>(false);
  const [productLines, setProductLines] = useState<any>();
  const [isRejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
  const [isCloseModalOpen, setCloseModalOpen] = useState<boolean>(false);
  const isContractLineActivated =
    instrumentationId !== undefined || instrumentationId !== null;
  const [hasActivated, setHasActivated] = useState<boolean>(false);
  const [isClosing, setClosing] = useState<boolean>(false);
  const [instrumentationStatus, setInstrumentationStatus] = useState<Map<number, InstrumentationProgress>>(new Map());
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isGettingInstrumentationIdRef = useRef<boolean>(false);

  const skipOneClickUi = shouldSkipOneClickInstrumentationUi(
    isGuaranteesEnabled,
    isOneClickEnabled
  );

  const getErrorMessage = getInstrumentationActivationErrorMessage;

  const handleGetInstrumentationId = useCallback(() => {
    // Nota: El guard se establece en el useEffect antes de llamar esta función
    // Aquí solo ejecutamos la lógica, sin verificar el guard nuevamente
    setIsLoading(true);
    
    getInstrumentationId(cuit, id)
      .then((response) => {
        // Verificar si la respuesta es exitosa
        if (response.success && hasData(response) && isSuccessfulResponse(response)) {
          setInstrumentationId(response.data.instrumentation_id);
        } else {
          // Manejar respuesta con error
          const errorResponse = response as { success: false; message?: string; code?: string; status?: number };
          const errorCode = errorResponse.code;
          const errorMessage = getErrorMessage(errorCode, errorResponse.message || 'Error desconocido al configurar garantías');
          
          useToast.error({
            title: errorMessage,
            id: 'instrumentation-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        }
      })
      .catch((error) => {
        console.error('Error getting instrumentation ID:', error);
        
        // Intentar extraer código y mensaje del error
        const errorCode = error.code || error.response?.data?.error || error.response?.data?.code;
        const errorMessage = error.message || error.response?.data?.message;
        const userMessage = getErrorMessage(errorCode, errorMessage || 'Error desconocido al configurar garantías');
        
        useToast.error({
          title: userMessage,
          id: 'instrumentation-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      })
      .finally(() => {
        setIsLoading(false);
        isGettingInstrumentationIdRef.current = false;
      });
  }, [cuit, id]);

  // useEffect 1: Cargar productLines solo una vez
  useEffect(() => {
    if (!productLines) {
      getProductLines().then((response) => {
        if (hasData(response) && isSuccessfulResponse(response)) {
          setProductLines(parseSelectableProductLines(response.data));
        }
      });
    }
  }, [productLines]);

  // useEffect 2: Actualizar estados derivados cuando cambian approved_products
  useEffect(() => {
    if (skipOneClickUi) {
      setIsAllActivated(true);
      setHasAnyNullInstrumentation(false);
      return;
    }

    if (!clientData.approved_products) return;

    const hasNullInstrumentation = (clientData.approved_products as Line[]).some(
      (line: Line) => line.instrumentation_id === null
    );
    setHasAnyNullInstrumentation(hasNullInstrumentation);

    const hasAllActivated = (clientData.approved_products as Line[]).every(
      (line: Line) => line.instrumentation_id !== null
    );
    setIsAllActivated(hasAllActivated);
  }, [clientData.approved_products, skipOneClickUi]);

  // useEffect 3: Llamar handleGetInstrumentationId solo cuando es necesario
  useEffect(() => {
    if (skipOneClickUi) {
      setIsLoading(false);
      return;
    }

    const hasInstrumentationId = clientData.instrumentation_id !== null &&
                                  clientData.instrumentation_id !== undefined &&
                                  clientData.instrumentation_id !== '';

    if (!hasInstrumentationId && !isGettingInstrumentationIdRef.current) {
      isGettingInstrumentationIdRef.current = true;
      handleGetInstrumentationId();
    } else if (hasInstrumentationId) {
      setIsLoading(false);
    }
  }, [clientData.instrumentation_id, handleGetInstrumentationId, skipOneClickUi]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const fetchInstrumentationStatus = useCallback(async () => {
    if (!id) return;

    try {
      const response = await getInstrumentationStatus(id);
      if (hasData(response) && isSuccessfulResponse(response)) {
        const statusMap = new Map<number, InstrumentationProgress>();
        response.data.product_lines.forEach((line: any) => {
          if (line.progress) {
            statusMap.set(line.id, line.progress);
          }
        });
        setInstrumentationStatus(statusMap);

        const hasFailedProgress = response.data.product_lines.some(
          (line: any) => line.progress?.status === InstrumentationStatus.FAILED
        );
        const hasNullInstrumentation = (clientData.approved_products as Line[] | undefined)?.some(
          (line: Line) => line.instrumentation_id === null
        );
        if (hasFailedProgress || hasNullInstrumentation) {
          setHasActivated(true);
        }

        const allFinished = response.data.product_lines.every(
          (line: any) =>
            line.status === InstrumentationStatus.SUCCESS ||
            line.status === InstrumentationStatus.FAILED
        );

        if (allFinished) {
          stopPolling();
        }

        return statusMap;
      }
    } catch (error) {
      console.error('Error fetching instrumentation status:', error);
      stopPolling();
    }
    return undefined;
  }, [id, clientData.approved_products, stopPolling]);

  const startPolling = useCallback(() => {
    setIsPolling((current) => {
      if (current) return current;
      pollingIntervalRef.current = setInterval(() => {
        fetchInstrumentationStatus();
      }, 2000);
      return true;
    });
  }, [fetchInstrumentationStatus]);

  // OneClick: restaurar estado de activación tras recarga
  useEffect(() => {
    if (skipOneClickUi || !id) return;
    fetchInstrumentationStatus();
  }, [skipOneClickUi, id, fetchInstrumentationStatus]);

  // Polling effect
  useEffect(() => {
    const hasInProgress = Array.from(instrumentationStatus.values()).some(
      progress => progress?.status === InstrumentationStatus.IN_PROGRESS
    );

    if (hasInProgress && !isPolling) {
      startPolling();
    } else if (!hasInProgress && isPolling) {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [instrumentationStatus, isPolling, startPolling, stopPolling]);

  const handleActivateLines = () => {
    setIsActivatingLines(true);
    setHasActivated(true);
    
    activateClientLines(cuit, id)
      .then((response) => {
        // Verificar tanto success como status
        if (response.success && hasData(response) && isSuccessfulResponse(response)) {
          // Actualizar estado local con la respuesta
          const statusMap = new Map<number, InstrumentationProgress>();
          response.data.approved_product_lines.forEach((line: Line) => {
            if (line.instrumentation_progress) {
              statusMap.set(line.id, line.instrumentation_progress);
            }
          });
          setInstrumentationStatus(statusMap);

          const oneClickEnabled = response.data.oneclick_enabled !== false;
          setIsOneClickEnabled(oneClickEnabled);

          if (!oneClickEnabled) {
            setIsAllActivated(true);
            setHasAnyNullInstrumentation(false);
          } else {
            const hasNullInstrumentation =
              response.data.approved_product_lines.some(
                (line: Line) => line.instrumentation_id === null
              );
            setHasAnyNullInstrumentation(hasNullInstrumentation);
            const hasAllActivated = response.data.approved_product_lines.every(
              (line: Line) => line.instrumentation_id !== null
            );
            setIsAllActivated(hasAllActivated);
          }
        } else {
          // Manejar error con código específico
          const errorResponse = response as { success: false; message?: string; code?: string; status?: number };
          const errorCode = errorResponse.code;
          const errorMessage = getErrorMessage(errorCode, errorResponse.message);
          
          useToast.error({
            title: 'Error al activar líneas',
            id: 'instrumentation-toast',
            description: errorMessage,
            closeButton: true,
            autoClose: 5000,
          });
        }
      })
      .catch((error) => {
        console.error('Error activating lines:', error);
        
        // Intentar extraer código y mensaje del error
        const errorCode = error.code || error.response?.data?.error || error.response?.data?.code;
        const errorMessage = error.message || error.response?.data?.message;
        const userMessage = getErrorMessage(errorCode, errorMessage);
        
        useToast.error({
          title: 'Error al activar líneas',
          id: 'instrumentation-toast',
          description: userMessage,
          closeButton: true,
          autoClose: 5000,
        });
        
        // Forzar una actualización del estado para reflejar el error si es necesario
        fetchInstrumentationStatus();
      })
      .finally(() => {
        setIsActivatingLines(false);
      });
  };

  const InfoRow = ({
    label,
    value,
    fullWidth = false,
  }: {
    label: string;
    value: string | undefined;
    fullWidth?: boolean;
  }) => (
    <Card display='flex' width={fullWidth ? '100%' : '48%'} gap='8px'>
      <P type='2' weight='bold' variant='neutral'>
        {label}
      </P>
      <P type='2' variant='neutral'>
        {value}
      </P>
    </Card>
  );

  const ClientDataBox = () => {
    const { basic_data, cuit, referrer_name, officer } = clientData;
    const actividad = economicActivities.find(
      (e) => e.code === Number(basic_data.activity)
    )?.label;

    const ivaStatus = ivaStatuses.find(
      (i) => i.code === basic_data.iva_status
    )?.label;

    return (
      <Card>
        {!skipOneClickUi && (clientData.id_application === null || !isContractLineActivated) && (
          <Card
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            variant='warning'
            height='40px'
          >
            <Card display='flex'>
              <Image name='warning_line' variant='warning' modifier='strong' />
              <Card margin={{ left: '3' }}>
                <P type='2' variant='neutral' weight='bold'>
                  Hubo un error de comunicación con One Click: Volvé a
                  intentarlo.
                </P>
              </Card>
            </Card>
            <Image
              name='retry_data'
              variant='warning'
              modifier='strong'
              cursor='pointer'
              onClick={() => handleGetInstrumentationId()}
            />
          </Card>
        )}
        <P variant='neutral' type='1' weight='bold' modifier='strong'>
          Datos del cliente
        </P>
        <Card
          display='flex'
          flexWrap='wrap'
          spacing='3'
          variant='neutral'
          modifier='regular'
          margin={{ bottom: '4' }}
          withElevation
        >
          <InfoRow label='Razón social:' value={basic_data.company_name} />
          <InfoRow label='Referido/Vendedor:' value={referrer_name} />
          <InfoRow label='Oficial:' value={officer} />
          <InfoRow label={`${fiscalId.label}:`} value={fiscalId.format(cuit)} />
          <InfoRow
            label='Fecha de constitución:'
            value={basic_data.date_incorporation}
          />
          <InfoRow label='Personería:' value={basic_data.legal_entity} />
          <InfoRow label='Condición frente al IVA:' value={ivaStatus} />
          <InfoRow label='Actividad económica:' value={actividad} fullWidth />
          <Card display='flex'>
            <InfoRow
              label='Domicilio fiscal:'
              value={basic_data.company_address}
              fullWidth
            />
            <Card display='flex' width='580px' gap='16px'>
              <InfoRow label='Localidad:' value={basic_data.state} />
              <InfoRow label='Provincia:' value={basic_data.city} />
            </Card>
          </Card>
          <InfoRow label='Teléfono:' value={basic_data.phone_number} />
          <InfoRow label='Correo electrónico:' value={basic_data.email} />
          <Card
            variant='success'
            modifier='inverted'
            width='100%'
            justifyItems='center'
            border
            margin={{ top: '3' }}
            spacing={'3'}
          >
            {clientData.approved_amount &&
              clientData.approved_amount.map(
                (amount: amount, index: number) => (
                  <Card
                    key={index}
                    display='flex'
                    justifyContent='space-between'
                    width='20%'
                  >
                    <H
                      type='3'
                      weight='bold'
                      modifier='strong'
                      align='center'
                      withMargin={false}
                    >
                      {amount.currency_code}
                      {'  '} {formatCurrency(amount.value)}
                    </H>
                  </Card>
                )
              )}
            <Card margin={{ right: '2', top: '2' }}>
              <H type='4' weight='bold' modifier='strong' withMargin={false}>
                Valor de fianza: ARS {clientData.note_amount}
              </H>
              <H type='4' weight='bold' modifier='strong' withMargin={false}>
                Valor de pagaré: ARS {clientData.guarantee_amount}
              </H>
            </Card>
          </Card>
        </Card>
      </Card>
    );
  };

  const LineRow = ({ line }: { line: Line }) => {
    const progress = resolveLineProgress(line, instrumentationStatus);
    const isWarningLine = shouldShowLineWarning(
      skipOneClickUi,
      hasActivated,
      line.instrumentation_id
    );
    const isSuccessLine = isLineSuccessInOneClickMode(
      skipOneClickUi,
      line.instrumentation_id
    );
    const warningMessage = isWarningLine ? getLineWarningMessage(progress) : '';

    return (
      <Card
        display='flex'
        flexDirection='column'
        variant={
          isSuccessLine ? 'success' : isWarningLine ? 'warning' : 'neutral'
        }
        modifier='weak'
        margin={{ bottom: '3' }}
        withElevation
        spacing={'1'}
      >
        <Card
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          width='100%'
        >
          <Card width='35%' margin={{ left: '2' }}>
            <P type='1' weight='bold' modifier='strong' variant='neutral'>
              {productLines && line.product_line_code
                ? resolveSelectableProductLineLabel(productLines, line.product_line_code) ??
                  UNAVAILABLE_PRODUCT_LINE_LABEL
                : ''}
            </P>
          </Card>
          <Card width='35%' display='flex' gap='4px'>
            <P type='2' weight='bold' variant='neutral'>
              Moneda:
            </P>
            <P type='2' variant='neutral'>
              {line.amount.currency_code}
            </P>
          </Card>
          <Card width='15%' display='flex' gap='4px'>
            <P type='2' weight='bold' variant='neutral'>
              Monto:
            </P>
            <P type='2' variant='neutral'>
              {line.amount.value}
            </P>
          </Card>
          <Card width='15%' display='flex'>
            {isSuccessLine && !progress && (
              <Card display='flex' margin={{ left: '9' }}>
                <Card
                  display='flex'
                  justifyContent='flex-end'
                  margin={{ left: '3' }}
                >
                  <Image name='check_line' />
                </Card>
              </Card>
            )}
            {isWarningLine && !progress && (
              <Card display='flex' margin={{ left: '9' }}>
                <Card
                  display='flex'
                  justifyContent='flex-end'
                  margin={{ left: '3' }}
                >
                  <Image
                    name='warning_line'
                    variant='warning'
                    modifier='strong'
                  />
                </Card>
              </Card>
            )}
          </Card>
        </Card>
        {progress && (
          <Card margin={{ left: '2', top: '2' }}>
            <LineStatusIndicator progress={progress} />
          </Card>
        )}
        {isWarningLine && !progress && (
          <Card margin={{ left: '2', top: '2' }}>
            <P type='2' variant='warning' weight='bold' withMargin={false}>
              {warningMessage}
            </P>
          </Card>
        )}
      </Card>
    );
  };

  const hasOneClickLineWarnings =
    !skipOneClickUi &&
    lines?.some((line) =>
      shouldShowLineWarning(skipOneClickUi, hasActivated, line.instrumentation_id)
    );

  const LinesSection = () => (
    <Card variant='none' spacing='2' margin={{ bottom: '4' }}>
      <P variant='neutral' type='1' weight='bold' modifier='strong'>
        Líneas
      </P>
      {skipOneClickUi && (
        <Card
          variant='info'
          modifier='weak'
          spacing='3'
          margin={{ bottom: '3' }}
          borderRadiusAmount='4'
        >
          <P type='2' variant='neutral' withMargin={false}>
            {AVALES_INSTRUMENTATION_INFO_BANNER}
          </P>
        </Card>
      )}
      {hasOneClickLineWarnings && (
        <Card
          variant='warning'
          modifier='weak'
          spacing='3'
          margin={{ bottom: '3' }}
          borderRadiusAmount='4'
        >
          <P type='2' variant='neutral' withMargin={false}>
            {ONECLICK_LINES_SECTION_WARNING}
          </P>
        </Card>
      )}
      {lines?.map((line: Line, index: number) => (
        <LineRow key={index} line={line} />
      ))}
    </Card>
  );

  const handleApplication = (status: string, modalText: string) => {
    setClosing(true);
    const commentData: commentData = { status_code: status, comment: modalText };
    if (status === APPLICATION_STATUS.finished.label && vigencia?.startDate && vigencia?.endDate) {
      commentData.start_date = vigencia.startDate;
      commentData.end_date = vigencia.endDate;
    }
    updateApplication(
      cuit || '',
      commentData,
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud de este cliente.'
      );
      mapStatus.set(
        APPLICATION_STATUS.finished.label,
        'Cerraste la carpeta de este cliente.'
      );
      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'instrumentation-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, locationState);
        }, 1000);
        setClosing(false);
      } else if (response.status === 422 && response.message) {
        useToast.error({
          title: response.message,
          id: 'instrumentation-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        setClosing(false);
      } else {
        useToast.error({
          title: 'Ocurrió un error al cerrar la carpeta.',
          id: 'instrumentation-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        setClosing(false);
      }
    });
  };

  return (
    <Card variant='none' spacing='4'>
      <Toast id='instrumentation-toast' />
      {isLoading ? (
        <Card
          height='400px'
          alignItems='center'
          display='flex'
          justifyContent='center'
        >
          <Spinner size='md' variant='primary' />
        </Card>
      ) : (
        <Card>
          <RejectApplicationModal
            isOpen={isRejectModalOpen}
            onClose={() => setRejectModalOpen(false)}
            onSecondaryAction={() => setRejectModalOpen(false)}
            onPrimaryAction={(rejectText: string) => {
              setRejectModalOpen(false);
              handleApplication(APPLICATION_STATUS.rejected.label, rejectText);
            }}
            loadingPrimaryBtn={isClosing}
          />

          <CloseApplicationModal
            isOpen={isCloseModalOpen}
            onClose={() => setCloseModalOpen(false)}
            onSecondaryAction={() => setCloseModalOpen(false)}
            onPrimaryAction={(closeText: string) => {
              setCloseModalOpen(false);
              handleApplication(APPLICATION_STATUS.finished.label, closeText);
            }}
            loadingPrimaryBtn={isClosing}
          />
          <ClientDataBox />
          <LinesSection />
          {lines?.length > 0 && !skipOneClickUi && isContractLineActivated && !isAllActivated && (
            <Card
              display='flex'
              justifyContent='flex-end'
              gap='16px'
              margin={{ bottom: '4' }}
            >
              <Card width='220px'>
                {isActivatingLines ? (
                  <Card display='flex' justifyContent='center' alignItems='center' width='100%'>
                    <Spinner size='sm' variant='primary' />
                  </Card>
                ) : (
                  <Button
                    variant='secondary'
                    onClick={handleActivateLines}
                    fullWidth
                  >
                    {hasActivated && hasAnyNullInstrumentation
                      ? 'Reintentar'
                      : 'Activar'}
                  </Button>
                )}
              </Card>
            </Card>
          )}

          <Card display='flex' justifyContent='space-between'>
            <Card width='200px'>
              <Button 
                variant='secondary' 
                onClick={onPreviousClick} 
                fullWidth
              >
                Volver
              </Button>
            </Card>
            <Card display='flex' justifyContent='flex-end' gap='16px'>
              <Card width='200px'>
                <Button
                  variant='secondary'
                  onClick={() => setRejectModalOpen(true)}
                  fullWidth
                >
                  Declinar
                </Button>
              </Card>
              <Card width='200px'>
                <Button
                  variant='primary'
                  onClick={() => setCloseModalOpen(true)}
                  disabled={!isAllActivated}
                  fullWidth
                >
                  Cerrar carpeta
                </Button>
              </Card>
            </Card>
          </Card>
        </Card>
      )}
    </Card>
  );
};

export default ClientLines;
